import { CommodityDto } from '@billing/app/api'
import { EventTypes } from '@billing/messages/eventsTypes'
import { CatalogueStore } from '@billing/stores/CatalogueStore'
import { CommodityStore } from '@billing/stores/CommodityStore'
import { IMessageBus } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'
import { CommodityWithParentStatus } from '../models/commodity.model'
import { CommodityFilterUIStore } from './commodity-filter.ui-store'

export class CommodityListUIStore {
  searchCommodity = ''

  constructor(
    private readonly commodityStore: CommodityStore,
    private readonly catalogueStore: CatalogueStore,
    private readonly commodityFilterUIStore: CommodityFilterUIStore,

    messageBus: IMessageBus,
  ) {
    makeObservable(this, {
      searchCommodity: observable,
      commodities: computed,

      setSearchCommodity: action,
    })

    messageBus.subscribeEvent<boolean>(EventTypes.CommodityRefresh, this.commodityStore.loadAll)
  }

  public setSearchCommodity(commodity: string) {
    if (this.searchCommodity !== commodity) {
      this.searchCommodity = commodity
    }
  }

  public get commodities(): CommodityWithParentStatus[] {
    const filteredCommoditiesByCatalogue = this.commodityFilterUIStore.filterItems(
      this.commodityStore.items.filter(
        commodity => commodity.catalogueId === this.catalogueStore.selectedCatalogueId,
      ),
    )

    return filteredCommoditiesByCatalogue.map(commodity => ({
      ...commodity,
      isFirstLevel: true,
      childCommodities: this.propagateIsActive(
        commodity.childCommodities || [],
        commodity.isActive,
      ),
    }))
  }

  public async getNestedCommoditiesToCsv() {
    const data = this.commodities.map(commodity => commodity)
    const rows: any[] = []
    function flatten(node: CommodityWithParentStatus, parentCommodityCode: string) {
      const {
        id, // removed
        catalogueId, // removed
        parentCommodityId, // removed
        isFirstLevel, // removed
        isParentActive, // removed
        packageIds,
        attributes,
        childCommodities,
        ...rest
      } = node
      rows.push({
        ...rest,
        attributes: JSON.stringify(attributes ?? []),
        parentCommodityCode,
      })
      childCommodities?.forEach(child => flatten(child, node.code))
    }

    data.forEach(item => flatten(item, ''))

    const headers = Object.keys(rows[0])
    const csv = [
      headers.join(','),
      ...rows.map(row => headers.map(key => `"${String(row[key]).replace(/"/g, '""')}"`).join(',')),
    ]
    return csv.join('\n')
  }

  private propagateIsActive = (
    commodities: CommodityDto[],
    parentIsActive = true,
  ): CommodityWithParentStatus[] => {
    return commodities.map(commodity => {
      const effectiveIsActive = parentIsActive && commodity.isActive
      const updatedCommodity = {
        ...commodity,
        isActive: effectiveIsActive,
        isParentActive: parentIsActive,
        childCommodities: commodity.childCommodities
          ? this.propagateIsActive(commodity.childCommodities, effectiveIsActive)
          : [],
      }
      return updatedCommodity
    })
  }
}
